<!-- Dropdown Trigger -->
<button
  type="button"
  [zagScrollIntoView]="true"
  class="zag-dropdown__trigger"
  cdkOverlayOrigin
  #triggerOrigin="cdkOverlayOrigin"
  [id]="id()"
  [attr.name]="name()"
  [attr.aria-haspopup]="'listbox'"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-required]="required()"
  [attr.aria-disabled]="disabled()"
  [disabled]="disabled()"
  (click)="toggle()"
  (focus)="onTriggerFocus()"
  (blur)="onTriggerBlur()"
>
  <span class="zag-dropdown__value" [class.zag-dropdown__value--placeholder]="showPlaceholder()">
    @if (hasAvatarVisual(selectedOption())) {
    <zag-avatar
      class="zag-dropdown__avatar"
      [name]="selectedOption()?.avatarName ?? ''"
      [src]="resolveAvatarSrc(selectedOption())"
      size="sm"
    />
    } @else if (hasEntityVisual(selectedOption())) {
    <zag-entity-visual
      class="zag-dropdown__entity-visual"
      [icon]="$safeNavigationMigration(selectedOption()?.icon)"
      [color]="$safeNavigationMigration(selectedOption()?.color)"
      [imageUrl]="$safeNavigationMigration(selectedOption()?.imageUrl)"
      size="sm"
    />
    } @else if (selectedOption()?.color) {
    <span class="zag-dropdown__swatch" [style.background-color]="selectedOption()!.color"></span>
    } @else if (selectedOption()?.status) {
    <span class="zag-dropdown__status zag-dropdown__status--{{ selectedOption()!.status }}"></span>
    } @if (selectedOption()?.badge) {
    <span class="zag-dropdown__value-text">{{ displayText() }}</span>
    <span class="zag-dropdown__trigger-badge">{{ selectedOption()!.badge }}</span>
    } @else if (selectedOption()?.group; as group) {
    <span class="zag-dropdown__value-stack">
      <span class="zag-dropdown__value-primary">{{ group }}</span>
      <span class="zag-dropdown__value-secondary">{{ selectedOption()!.label }}</span>
    </span>
    } @else {
    <span class="zag-dropdown__value-text">{{ displayText() }}</span>
    }
  </span>
  <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -- Passive click-isolation container; keyboard support belongs to the projected content. -->
  <span class="zag-dropdown__trailing" (click)="$event.stopPropagation()">
    <ng-content select="[zagDropdownTrailing]"></ng-content>
  </span>
  @if (showClearButton()) {
  <button type="button" class="zag-dropdown__clear" [attr.aria-label]="clearAriaLabel()" (click)="clearSelection($event)">
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  </button>
  } @if (!readonly()) {
  <span class="zag-dropdown__arrow" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" [class.zag-dropdown__arrow--rotated]="isOpen()">
      <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  </span>
  }
</button>

<!-- Dropdown Panel (rendered in CDK overlay; fullscreen dialog with Apply on mobile) -->
<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="triggerOrigin"
  [cdkConnectedOverlayOpen]="isOpen()"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayBackdropClass]="isMobile() ? 'zag-picker-dialog-backdrop' : 'zag-dropdown-backdrop'"
  [cdkConnectedOverlayPanelClass]="isMobile() ? 'zag-picker-dialog-panel' : ''"
  [cdkConnectedOverlayPositions]="isMobile() ? mobileOverlayPositions : overlayPositions"
  [cdkConnectedOverlayPush]="!isMobile() && panelWidth() === 'content'"
  (backdropClick)="isMobile() ? closeMobile() : close()"
  (detach)="isMobile() ? closeMobile() : close()"
>
  <div
    class="zag-dropdown__panel"
    [class.zag-dropdown__panel--sm]="size() === 'sm'"
    [class.zag-dropdown__panel--lg]="size() === 'lg'"
    [class.zag-dropdown__panel--content]="!isMobile() && panelWidth() === 'content'"
    [class.zag-dropdown__panel--mobile]="isMobile()"
    [style.width.px]="!isMobile() && panelWidth() === 'trigger' ? triggerOrigin.elementRef.nativeElement.offsetWidth : null"
    [style.--zag-dropdown-trigger-width.px]="!isMobile() && panelWidth() === 'content' ? triggerOrigin.elementRef.nativeElement.offsetWidth : null"
    [attr.role]="isMobile() ? 'dialog' : 'listbox'"
    [attr.aria-modal]="isMobile() ? 'true' : null"
    [attr.aria-multiselectable]="multiple() && !isMobile() ? true : null"
    [attr.aria-label]="ariaLabel() ?? placeholder()"
    [attr.aria-activedescendant]="!isMobile() && highlightedIndex() >= 0 ? 'option-' + highlightedIndex() : null"
  >
    @if (isMobile()) {
    <div class="zag-picker-mobile-header">
      <span class="zag-picker-mobile-header__label">{{ mobileTitle() }}</span>
      <button type="button" class="zag-picker-mobile-header__close" [attr.aria-label]="'picker.close' | translate" (click)="closeMobile()">
        <span zagIcon="close"></span>
      </button>
    </div>
    } @if (searchable()) {
    <div class="zag-dropdown__search">
      <span class="zag-dropdown__search-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <input
        #searchInput
        type="text"
        class="zag-dropdown__search-input"
        autocomplete="off"
        [attr.aria-label]="searchAriaLabel()"
        [placeholder]="searchPlaceholder()"
        [value]="searchTerm()"
        (input)="onSearchInput($any($event.target).value)"
        (keydown)="onKeyDown($event)"
        (click)="$event.stopPropagation()"
      />
      <div class="zag-dropdown__search-action">
        <ng-content select="[zagDropdownSearchAction]"></ng-content>
      </div>
    </div>
    }
    <div
      class="zag-dropdown__options"
      [attr.role]="isMobile() ? 'listbox' : null"
      [attr.aria-multiselectable]="isMobile() && multiple() ? true : null"
      [attr.aria-label]="isMobile() ? (ariaLabel() ?? placeholder()) : null"
    >
      @for (option of displayedOptions(); track option.value; let i = $index) { @if (groupHeaderAt(i); as groupLabel) {
      <div class="zag-dropdown__group-header" role="presentation" aria-hidden="true">{{ groupLabel }}</div>
      }
      <div
        [id]="'option-' + i"
        class="zag-dropdown__option"
        [class.zag-dropdown__option--selected]="isSelected(option)"
        [class.zag-dropdown__option--highlighted]="isHighlighted(i)"
        [class.zag-dropdown__option--disabled]="option.disabled"
        role="option"
        tabindex="-1"
        [attr.aria-label]="option.label"
        [attr.aria-selected]="isSelected(option)"
        [attr.aria-disabled]="option.disabled"
        (click)="selectOption(option)"
        (keydown.enter)="selectOption(option)"
        (keydown.space)="selectOption(option)"
        (mouseenter)="onOptionMouseEnter(i)"
      >
        @if (hasAvatarVisual(option)) {
        <zag-avatar class="zag-dropdown__avatar" [name]="option.avatarName ?? ''" [src]="resolveAvatarSrc(option)" size="sm" />
        } @else if (hasEntityVisual(option)) {
        <zag-entity-visual
          class="zag-dropdown__entity-visual zag-dropdown__entity-visual--option"
          [icon]="option.icon"
          [color]="option.color"
          [imageUrl]="option.imageUrl"
          size="sm"
        />
        } @else if (option.color) {
        <span class="zag-dropdown__swatch" [style.background-color]="option.color"></span>
        } @else if (option.status) {
        <span class="zag-dropdown__status zag-dropdown__status--{{ option.status }}"></span>
        }
        <span class="zag-dropdown__option-label">{{ option.label }}</span>
        @if (option.badge) {
        <span class="zag-dropdown__option-badge">{{ option.badge }}</span>
        }
        <span class="zag-dropdown__option-check" [class.zag-dropdown__option-check--invisible]="!isSelected(option)" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
          </svg>
        </span>
      </div>
      } @if (showNoResults()) {
      <div class="zag-dropdown__no-results" role="presentation">{{ noResultsText() }}</div>
      }
    </div>
    @if (isMobile()) {
    <div class="zag-picker-mobile-footer">
      <button type="button" class="zag-picker-mobile-footer__apply" [disabled]="!canApplyMobile()" (click)="applyMobile()">
        {{ 'picker.apply' | translate }}
      </button>
    </div>
    }
  </div>
</ng-template>
